import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { DOCUMENT_CONFIGS } from "../../../models/superadmin";
import { useAppDispatch, useAppSelector } from "../../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { getProfileDetailsThunk, profileDocumentRenewRequest } from "../../../store/onboarding/thunk";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { getDateLabel, getMaxDateByDocumentType, getMinDateByDocumentType } from "../../../utils/documentHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import CustomDateInput from "../../UI/Inputs/DateInput";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

interface RenewDocumentRequestModalProps {
  documentType: string;
  setShowRenewalModal: (value: boolean) => void;
}

const RenewDocumentRequestModal: React.FC<RenewDocumentRequestModalProps> = ({
  documentType,
  setShowRenewalModal
}) => {
  const inProgress = useAppSelector((state) => state.onboarding.inProgress);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const dispatch = useAppDispatch();

  /**
   *  Get the document name from the DOCUMENT_CONFIGS based on the documentType prop
   * @returns  string - Document name
   */
  const getDocumentName = (): string => {
    const docTypeName = DOCUMENT_CONFIGS.find( (docList) => docList.type === documentType);
    return docTypeName ? docTypeName.title : documentType.replace(/_/g, " ");
  }


  /**
   * Formik hook for form state management and validation
   * - Uses Yup validation schema defined in createValidationSchema.ts
   * - Handles form submission to dispatch Redux thunk for making the request
   * - Custom validation to ensure at least one document is uploaded
   */
  const {
    handleSubmit,
    values,
    errors,
    setFieldValue,
    setFieldError,
    setFieldTouched
  } = useFormik({
    validationSchema: createValidationSchema("profile_document_renew"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      document_date: "",
      document_type: documentType,
      attachment: "",
    },
    onSubmit: async () => {
      // Check if uploadedAttachments is empty, no need to pass it in the payload so added custom validation here
      if (!pendingFile) {
        toast.error("Please upload at least one document.");
        // return; // Prevent submission
      }
      // Format the date to YYYY-MM-DD before submitting
      let formattedDate = values.document_date;
      if (formattedDate) {
        const dateObj = new Date(formattedDate);
        formattedDate = dateObj.toISOString().slice(0, 10); // "YYYY-MM-DD"
      }
      // Prepare the payload without uploadedAttachments
      const formData = new FormData();
      formData.append("document_type", values.document_type);
      formData.append("document_date", formattedDate);
      if (pendingFile) {
        formData.append("attachment", pendingFile); // Append the actual file
      }

      try {
        const result = await dispatch(profileDocumentRenewRequest(formData));
        if (isFulfilled(result)) {
          setShowRenewalModal(false);
          dispatch(showNotificationAction("Submitted"));    
          setTimeout(() => {
            refreshProfileDetails();
          }, NOTIFICATION_TIME_OUT);      
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  }); 

  const refreshProfileDetails = async () => {
    dispatch(showTransparentPreloaderAction())
    await dispatch(getProfileDetailsThunk())
    dispatch(hideTransparentPreloaderAction())
  }


  const handleFileValidated = (file: File, fileName: string) => {
    setPendingFile(file);
    setFieldValue("attachment", file); // Store actual file object
    setFieldError("attachment", ""); // Clear error
    setFieldTouched("attachment", true); // Mark as touched
  };



  return (
    <div className="d-flex flex-column gap-20 fw-400 text-black">
      <div className="d-flex flex-column gap-15">
        <div className="fs-22">Request a Profile Edit</div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16">You are submitting a valid document for:</div>
        </div>
        <div className="d-flex flex-column gap-10">
         
          <div className="fs-16px fw-700">{getDocumentName()}</div>
          {/* Render DocumentUpload components dynamically */}
          <DocumentUpload
            type={documentType}  
            showFileUploadLabel={true}      
            dontUploadImmediately={true}   
            onFileValidated={handleFileValidated} 
            required={true} // First upload is required
          />
          {errors && <ErrorMessage message={errors.attachment as string} />}
          
        </div>
        <div className="d-flex gap-10 align-items-center">
          <div className="fs-13 fw-400 text-grey-dark">{getDateLabel(documentType)}:</div>
            <div className="w-200px">
            <DatePicker
              selected={values.document_date ? new Date(values.document_date) : null}
              onChange={(date: Date | null) => {
                if (date) {
                  // Store as YYYY-MM-DD format to avoid timezone issues
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const localDateString = `${year}-${month}-${day}`;
                  setFieldValue("document_date", localDateString);
                } else {
                  setFieldValue("document_date", "");
                }
                setFieldError("document_date", ""); // Clear error
                setFieldTouched("document_date", true); // Mark as touched
              }}
              customInput={
              <CustomDateInput
                placeholder="Select a date..."
                value={
                values.document_date
                  ? new Date(values.document_date).toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  : ""
                }
              />
              }
              dateFormat="dd MMM yyyy"
              minDate={getMinDateByDocumentType(documentType)}
              maxDate={getMaxDateByDocumentType(documentType)}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
            />
            </div>
            
        </div>
        {errors && <ErrorMessage message={errors.document_date as string} />}
      </div>
      <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center">
        <Warning />
        <div className="fs-13 fw-400 text-black lh-sm">
          Please allow up to 1 business day for one of our consultants to review
          and approve changes.
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={handleSubmit}
          isLoading={inProgress}
          contentClass="d-flex"
        >
          <RoundedTick color={inProgress ? "#aaaaaa" : "black"} />
          Make Request
        </CustomButton>
      </div>
    </div>
  );
};

export default RenewDocumentRequestModal;
